import { Dispatch, SetStateAction, useState } from "react";
import { Badge, Button, Flex, FormControl, FormLabel, Input, Select, SimpleGrid, useToast } from "@chakra-ui/react";
import { FiscalParty, getFiscalParty } from "../../../services/fiscalService";
import { getCnpjData } from "../../../services/cnpjService";
import { crm } from "../../../theme";

export const emptyParty = (): FiscalParty => ({ document: "", name: "", ieIndicator: 1 });

type LookupMessage = { status: "info" | "warning" | "error"; title: string; description?: string };

/**
 * Cadastro fiscal salvo para o CNPJ/CPF; sem cadastro, dados públicos da Receita (BrasilAPI) partindo do zero
 * (IE/e-mail/telefone de outro CNPJ não podem ficar). Party null = não achou (message diz por quê).
 */
export const lookupParty = async (document: string): Promise<{ party: FiscalParty | null; message?: LookupMessage }> => {
  const doc = document.replace(/\D/g, "");
  try {
    const saved = await getFiscalParty(doc);
    if (saved) return { party: saved };
    if (doc.length !== 14) return { party: null, message: { status: "info", title: "CPF sem cadastro: preencha os dados" } };
    const { data, error } = await getCnpjData(doc);
    if (!data) return { party: null, message: { status: "warning", title: error ?? "CNPJ não encontrado" } };
    return {
      party: {
        ...emptyParty(),
        document: doc,
        name: (data.razao_social ?? "").slice(0, 60),
        street: data.logradouro ?? null,
        number: data.numero ?? null,
        complement: data.complemento ?? null,
        district: data.bairro ?? null,
        city: data.municipio ?? null,
        cityCode: data.codigo_municipio_ibge ? String(data.codigo_municipio_ibge) : null,
        state: data.uf ?? null,
        zipCode: data.cep ?? null,
        saved: false,
      },
      message: { status: "info", title: "Dados da Receita carregados", description: "Confira e informe a inscrição estadual." },
    };
  } catch {
    return { party: null, message: { status: "error", title: "Não foi possível buscar o CNPJ" } };
  }
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

/**
 * Destinatário/transportadora da nota. "Buscar" usa o cadastro fiscal salvo (mesmo CNPJ numa nota anterior);
 * sem cadastro, puxa os dados públicos da Receita (BrasilAPI) e a pessoa completa a IE.
 */
const FiscalPartyFields = ({ value, onChange, carrier }: { value: FiscalParty; onChange: Dispatch<SetStateAction<FiscalParty>>; carrier?: boolean }) => {
  const toast = useToast();
  const [searching, setSearching] = useState(false);
  const set = (field: keyof FiscalParty, v: any) => onChange((p) => ({ ...p, [field]: v }));
  /** Resultado da busca só vale se o documento não mudou enquanto buscava. */
  const sameDoc = (p: FiscalParty, doc: string) => p.document.replace(/\D/g, "") === doc;

  const search = async () => {
    const doc = value.document.replace(/\D/g, "");
    if (doc.length !== 14 && doc.length !== 11) {
      toast({ status: "warning", title: "Informe um CNPJ (14 dígitos) ou CPF (11)" });
      return;
    }
    setSearching(true);
    try {
      const { party, message } = await lookupParty(doc);
      if (party) onChange((p) => (sameDoc(p, doc) ? party : p));
      if (message) toast({ status: message.status, title: message.title, description: message.description });
    } finally {
      setSearching(false);
    }
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
      <FormControl>
        <Label>CNPJ/CPF {value.saved && <Badge ml="4px" colorScheme="green" fontSize="9px">cadastro salvo</Badge>}</Label>
        <Flex gap="6px">
          <Input size="sm" maxLength={18} value={value.document} onChange={(e) => set("document", e.target.value)} />
          <Button size="sm" variant="outline" isLoading={searching} onClick={search}>Buscar</Button>
        </Flex>
      </FormControl>
      <FormControl gridColumn={{ md: "span 2" }}>
        <Label>{carrier ? "Transportadora" : "Razão social"}</Label>
        <Input size="sm" maxLength={60} value={value.name} onChange={(e) => set("name", e.target.value)} />
      </FormControl>
      <FormControl>
        <Label>Inscrição estadual</Label>
        <Input size="sm" maxLength={20} value={value.stateRegistration ?? ""} onChange={(e) => set("stateRegistration", e.target.value)} />
      </FormControl>
      {!carrier && (
        <FormControl>
          <Label>Contribuinte</Label>
          <Select size="sm" value={value.ieIndicator} onChange={(e) => set("ieIndicator", Number(e.target.value))}>
            <option value={1}>1 · contribuinte ICMS</option>
            <option value={2}>2 · isento</option>
            <option value={9}>9 · não contribuinte</option>
          </Select>
        </FormControl>
      )}
      <FormControl gridColumn={{ md: carrier ? "span 2" : undefined }}>
        <Label>Logradouro</Label>
        <Input size="sm" maxLength={60} value={value.street ?? ""} onChange={(e) => set("street", e.target.value)} />
      </FormControl>
      <FormControl>
        <Label>Número</Label>
        <Input size="sm" maxLength={60} value={value.number ?? ""} onChange={(e) => set("number", e.target.value)} />
      </FormControl>
      {!carrier && (
        <FormControl>
          <Label>Complemento</Label>
          <Input size="sm" maxLength={60} value={value.complement ?? ""} onChange={(e) => set("complement", e.target.value)} />
        </FormControl>
      )}
      <FormControl>
        <Label>Bairro</Label>
        <Input size="sm" maxLength={60} value={value.district ?? ""} onChange={(e) => set("district", e.target.value)} />
      </FormControl>
      <FormControl>
        <Label>Município</Label>
        <Input size="sm" maxLength={60} value={value.city ?? ""} onChange={(e) => set("city", e.target.value)} />
      </FormControl>
      {!carrier && (
        <FormControl>
          <Label>Código IBGE</Label>
          <Input size="sm" maxLength={7} value={value.cityCode ?? ""} onChange={(e) => set("cityCode", e.target.value)} />
        </FormControl>
      )}
      <FormControl>
        <Label>UF</Label>
        <Input size="sm" maxLength={2} value={value.state ?? ""} onChange={(e) => set("state", e.target.value.toUpperCase())} />
      </FormControl>
      <FormControl>
        <Label>CEP</Label>
        <Input size="sm" maxLength={9} value={value.zipCode ?? ""} onChange={(e) => set("zipCode", e.target.value)} />
      </FormControl>
      {!carrier && (
        <FormControl gridColumn={{ md: "span 2" }}>
          <Label>E-mail (recebe o XML)</Label>
          <Input size="sm" maxLength={200} value={value.email ?? ""} onChange={(e) => set("email", e.target.value)} />
        </FormControl>
      )}
    </SimpleGrid>
  );
};

export default FiscalPartyFields;
